import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import CustomTable from "../../../../../Components/common/Table/CustomTable";
import {
  DetailEmpty,
  DetailField,
  DetailGrid,
  DetailPanel,
  DetailSection,
  DetailValue,
} from "../../../../../Components/common/DetailPanel/DetailPanel";
import { overview, overviewReport } from "../../../../../utils/constants/tableHeaders";
import { RequestP2signatureList } from "../../../../../store/actions/analysisReportActions";

import "./styles.scss";
import Backdrop from "../../../../../Components/UI/Backdrop/Backdrop";

//GET FILE ID IN URL
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const Overview = ({ report = false }) => {
  const dispatch = useDispatch();

  const overviewRef = useRef(null);

  //SET QUERY FOR GETTING DATA ID
  let query = useQuery();

  const signatureList = useSelector((state) => state.analysisReports.signatureList);
  const p2signatureList = useSelector((state) => state.analysisReports.p2signatureList);
  const isSignaturesLoading = useSelector((state) => state.analysisReports.signaturesLoading);
  const isP2SignaturesLoading = useSelector((state) => state.analysisReports.p2signaturesloading);

  useEffect(() => {
    dispatch(RequestP2signatureList(query.get("data")));
  }, [query.get("data")]);

  const getSignatureFields = (signatureList) => {
    let newSignatureList = [];

    if (signatureList && Array.isArray(signatureList)) {
      newSignatureList = [
        ...signatureList.map((signature) => {
          let ttpValues = [];
          if (Array.isArray(signature.ttp)) ttpValues = signature.ttp;
          else if (signature.ttp !== null && typeof signature.ttp === "object")
            ttpValues = Object.keys(signature.ttp);
          return {
            ...signature,
            ttp: ttpValues,
          };
        }),
      ];
    }

    return newSignatureList;
  };

  const getUniqueP2Signatures = (signatures_, p2signatures_) => {
    const newP2SignatureList = p2signatures_.filter(
      (p2Signature) =>
        !signatures_.some(
          (signature) => signature.name && p2Signature.same && signature.name === p2Signature.same,
        ),
    );

    return newP2SignatureList;
  };

  // A matched rule (e.g. a sigma rule) is headed by its title, which then is not
  // repeated in its fields; other records are headed by their number
  const matchHeading = (detail, index) => {
    const isRecord = detail && typeof detail === "object" && !Array.isArray(detail);
    const title = isRecord && typeof detail.title === "string" ? detail.title.trim() : "";
    if (!title) return { title: `Match ${index + 1}`, value: detail };
    const { title: _title, ...rest } = detail;
    return { title, value: Object.keys(rest).length ? rest : detail };
  };

  // Signature matches: the analyst's explanation first, then each matched record
  // in its most readable form (fields for flat records, a JSON tree for nested ones)
  const Details = React.useCallback(({ row }) => {
    const details = row?.original?.data;
    const information = row?.original?.information;

    return (
      <DetailPanel>
        <DetailGrid columns={1}>
          <DetailField label="Information">{information || "No information found"}</DetailField>
        </DetailGrid>
        <DetailSection title="Matched data" count={details?.length || 0}>
          {details && details.length > 0 ? (
            details.map((detail, i) => {
              const heading = matchHeading(detail, i);
              return <DetailValue key={i} value={heading.value} title={heading.title} />;
            })
          ) : (
            <DetailEmpty>No details found</DetailEmpty>
          )}
        </DetailSection>
      </DetailPanel>
    );
  }, []);

  return (
    <div
      className={
        report
          ? "overview__table-block overview__table-block-report"
          : "overview__table-block overview__table-block-main"
      }>
      <Backdrop show={isSignaturesLoading || isP2SignaturesLoading} absolute={true} />
      {!isSignaturesLoading && !isP2SignaturesLoading && (
        <CustomTable
          dataOverview={[
            ...getSignatureFields(signatureList),
            ...getUniqueP2Signatures(
              getSignatureFields(signatureList),
              getSignatureFields(p2signatureList),
            ),
          ].sort((a, b) => b.severity - a.severity)}
          tableName={"overview"}
          column={report ? overviewReport : overview}
          refresh={!report}
          exportFile={!report}
          paginationButtons={!report}
          customPageSize={report ? 100 : 10}
          tableRef={overviewRef}
          leftAction
          renderRowSubComponent={!report && Details}
          minHeight={report ? "auto" : "585px"}
          tableFolder={"analysisReports"}
          minBottom="0px"
          paginationBorder={!report}
          paginationHeight="38px"
          defaultPosition="relative"
          reportPage={report}
          // tableBorder is inverted: true is what removes the border from the
          // table element. The default is true; passing !report here left the
          // table with its own border inside the wrapper's, so two showed up.
        />
      )}
    </div>
  );
};

export default Overview;
